// DownloadSystemLogsPDF.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ColorLogo from "../assets/login/ColorLogo.png";
import logo1 from "../assets/header/HABD.png";
import logo2 from "../assets/login/DMW_Patiala.png";
import logo3 from "../assets/login/NoviusLogo.png"; // Watermark logo

// Helper functions for formatting (same as in LogSearch component)
const formatSourceScript = (script) => {
  if (!script) return '';
  
  // Remove .js extension
  let formatted = script.replace(/\.js$/, '');
  
  // Special cases
  if (formatted === 'HabdStatus') return 'HABD Status';
  if (formatted === 'TemperatureStatus') return 'Temperature Status';
  if (formatted === 'ProxyStatus') return 'Proxy Status';
  if (formatted === 'PlcStatus') return 'PLC Status';
  if (formatted === 'InternetConnectivity') return 'Internet Connectivity';
  if (formatted === 'RestartStatus') return 'Restart Status';
  
  // Default: insert space before capital letters and capitalize first letter
  formatted = formatted.replace(/([A-Z])/g, ' $1')
                       .replace(/^./, str => str.toUpperCase())
                       .trim();
  
  return formatted;
};

const formatCategory = (category) => {
  if (!category) return 'N/A';
  
  return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear();
  
  // Format time as "12:38 PM"
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert to 12-hour format
  
  return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
};

const formatAdditionalData = (data) => {
  if (!data) return 'N/A';
  try {
    const parsed = JSON.parse(data);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return data;
  }
};

const DownloadSystemLogsPDF = async (
  selectedSite,
  selectedSiteName,
  systemLogs,
  timePeriod,
  setIsLoading
) => {
  try {
    setIsLoading(true);
    
    const user = JSON.parse(localStorage.getItem("user"));
    const userName = user?.username || "Guest";
    const downloadDate = new Date();
    const formattedDateTime = formatDate(downloadDate); // Using the same format as table
    
    // Calculate date range for time period
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - parseInt(timePeriod));
    
    const formattedFromDate = formatDate(fromDate);
    const formattedToDate = formatDate(toDate);
    
    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const logoWidth = 18;
    const logoHeight = 15;
    
    const addHeader = () => {
      doc.setTextColor(0, 0, 0);
      if (ColorLogo) {
        const logoX = 10;
        const logoY = 5;
        const logoW = 35;
        const logoH = logoHeight;
        doc.addImage(ColorLogo, "PNG", logoX, logoY, logoW, logoH);
        doc.link(logoX, logoY, logoW, logoH, {
          url: "https://noviusrailtech.com/",
        });
      }
      if (logo2) {
        doc.addImage(
          logo2,
          "PNG",
          pageWidth - logoWidth - 10,
          5,
          logoWidth,
          logoHeight
        );
      }
      const titleText =
        "NOVIUS HABD (Lite) - NOVIUS HOT AXLE BOX DETECTION SYSTEM (LITE MODEL)";
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      const textWidth = doc.getTextWidth(titleText);
      const centerX = pageWidth / 2;
      const offsetX = 20;
      const textX = centerX - textWidth / 2 + offsetX;
      if (logo1) {
        const logo1X = textX - logoWidth - 4;
        doc.addImage(logo1, "PNG", logo1X, 5, logoWidth, logoHeight);
      }
      doc.text(titleText, textX, 15);
      doc.setDrawColor(200, 200, 200);
      doc.line(10, 22, pageWidth - 10, 22);
    };
    
    const addFooter = () => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        const pageStr = `Page ${i} of ${pageCount}`;
        doc.text(
          pageStr,
          pageWidth - 14 - doc.getTextWidth(pageStr),
          pageHeight - 10
        );
        doc.setTextColor(80);
        doc.setFont("helvetica", "italic");
        const footerText = `Downloaded by: ${userName} | Download Time: ${formattedDateTime}`;
        doc.text(footerText, 14, pageHeight - 10);
      }
    };
    
    const addWatermark = () => {
      if (logo3) {
        const watermarkWidth = 150;
        const watermarkHeight = 100;
        const watermarkX = (pageWidth - watermarkWidth) / 2;
        const watermarkY = (pageHeight - watermarkHeight) / 2;
        doc.saveGraphicsState();
        doc.setGState(new doc.GState({ opacity: 0.08 })); // Subtle watermark
        doc.addImage(logo3, "PNG", watermarkX, watermarkY, watermarkWidth, watermarkHeight);
        doc.restoreGraphicsState();
      }
    };
    
    // Add initial header
    addHeader();
    let lastTableY = 40;
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`Site: ${selectedSiteName || "N/A"}`, 14, lastTableY);
    lastTableY += 10;
    
    // Update time period to show date range
    doc.text(`Time Period: ${formattedFromDate} - ${formattedToDate}`, 14, lastTableY);
    lastTableY += 10;
    
    doc.text(`Generated on: ${formattedDateTime}`, 14, lastTableY);
    lastTableY += 15;
    
    doc.text("System Logs", 14, lastTableY);
    lastTableY += 10;
    
    if (systemLogs?.length > 0) {
      const logColumns = [
        { content: "Sr. No.", styles: { halign: "center" } },
        { content: "Log Level", styles: { halign: "center" } },
        { content: "Source Script", styles: { halign: "center" } },
        { content: "Category", styles: { halign: "center" } },
        { content: "Message", styles: { halign: "left" } },
        { content: "Timestamp", styles: { halign: "center" } },
        { content: "Additional Data", styles: { halign: "left" } },
      ];
      
      const logBody = systemLogs.map((log, index) => [
        { content: index + 1 },
        { content: log.LogLevel || "N/A" },
        { content: formatSourceScript(log.SourceScript) },
        { content: formatCategory(log.Category) },
        { content: log.Message || "N/A" },
        { content: log.LogTimestamp ? formatDate(log.LogTimestamp) : "N/A" },
        { content: formatAdditionalData(log.AdditionalData) },
      ]);
      
      // Calculate total width available for columns
      const totalWidth = pageWidth - 20; // Subtract left and right margins (10 each)
      
      autoTable(doc, {
        startY: lastTableY,
        margin: { left: 10, right: 10, top: 25 }, // Added top margin to ensure gap
        head: [logColumns],
        body: logBody,
        theme: "grid",
        styles: {
          fontSize: 9,
          cellPadding: 3,
          textColor: [0, 0, 0],
          fillColor: [255, 255, 255],
          valign: "middle",
          overflow: "linebreak", // Handle text overflow
        },
        headStyles: {
          fillColor: [220, 220, 220],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: totalWidth * 0.05, halign: "center" }, // Sr. No.
          1: { cellWidth: totalWidth * 0.10, halign: "center" }, // Log Level
          2: { cellWidth: totalWidth * 0.15, halign: "center" }, // Source Script
          3: { cellWidth: totalWidth * 0.10, halign: "center" }, // Category
          4: { cellWidth: totalWidth * 0.30, halign: "left" },   // Message
          5: { cellWidth: totalWidth * 0.15, halign: "center" }, // Timestamp
          6: { cellWidth: totalWidth * 0.15, halign: "left" },   // Additional Data
        },
        // Prevent row splitting across pages
        rowPageBreak: 'avoid',
        // Ensure that if a row doesn't fit, it moves entirely to the next page
        pageBreak: 'auto',
        // Set minimum row height to ensure consistent appearance
        minRowHeight: 10,
        didDrawPage: (data) => {
          // Add header and watermark to every page
          addHeader();
          addWatermark();
          
          // Set the startY for the next table to be right after the header
          if (data.pageNumber > 1) {
            data.table.startY = 40; // Consistent gap between header and table on all pages
          }
        },
        didDrawTable: (data) => {
          lastTableY = data.table.finalY + 10;
        },
        // Hook to handle row height calculation
        willDrawCell: (data) => {
          // Calculate the required height for the cell content
          const cellContent = data.cell.raw;
          if (typeof cellContent === 'string') {
            const textLines = doc.splitTextToSize(cellContent, data.cell.width - 6);
            const requiredHeight = textLines.length * 4 + 6; // Approximate height calculation
            
            // Ensure the row height is sufficient
            if (data.row.height < requiredHeight) {
              data.row.height = requiredHeight;
            }
          }
        },
      });
    } else {
      doc.text("No system logs data available", 14, lastTableY);
      addWatermark();
    }
    
    addFooter();
    doc.save(`System_Logs_${selectedSiteName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (error) {
    console.error("Error generating System Logs PDF:", error);
    throw error;
  } finally {
    setIsLoading(false);
  }
};

export default DownloadSystemLogsPDF;